import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Minus, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getDishSupplements } from '../../services/supplementApi';
import namkeFallback from '../../assets/namke-fallback.png';

const DishPopup = ({
  dish,
  isOpen,
  onClose,
  onAddToCart
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [selectedSupplements, setSelectedSupplements] = useState({});
  const [supplements, setSupplements] = useState([]);
  const [supplementsLoading, setSupplementsLoading] = useState(false);

  // Réinitialiser l'état et charger les suppléments quand le plat change
  useEffect(() => {
    setQuantity(1);
    setSelectedSupplements({});
    setSupplements([]);

    if (!dish?.id) return;

    let cancelled = false;
    const load = async () => {
      setSupplementsLoading(true);
      try {
        const data = await getDishSupplements(dish.id);
        if (!cancelled) setSupplements(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setSupplements([]);
      } finally {
        if (!cancelled) setSupplementsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [dish?.id]);

  if (!isOpen || !dish) return null;

  const handleSupplementToggle = (supplementId) => {
    setSelectedSupplements(prev => ({
      ...prev,
      [supplementId]: !prev[supplementId]
    }));
  };

  const calculateTotalPrice = () => {
    const basePrice = dish.price || 0;
    const supplementsPrice = Object.entries(selectedSupplements)
      .filter(([, selected]) => selected)
      .reduce((total, [supplementId]) => {
        const supplement = supplements.find(s => s.id === parseInt(supplementId));
        return total + (supplement?.price || 0);
      }, 0);
    return (basePrice + supplementsPrice) * quantity;
  };

  const handleAddToCart = () => {
    const chosenSupplements = Object.entries(selectedSupplements)
      .filter(([, selected]) => selected)
      .map(([supplementId]) => supplements.find(s => s.id === parseInt(supplementId)))
      .filter(Boolean);

    const cartItem = {
      ...dish,
      quantity,
      supplements: chosenSupplements,
      totalPrice: calculateTotalPrice(),
    };
    if (onAddToCart) {
      onAddToCart(cartItem);
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header image */}
        <div className="relative">
          <img
            src={dish.image_url || dish.dish_image_url || namkeFallback}
            alt={dish.name}
            className="w-full h-64 object-cover rounded-t-2xl"
            onError={(e) => {
              e.target.src = namkeFallback;
            }}
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <h2 className="text-2xl font-bold mb-1" style={{ color: 'rgb(5, 70, 37)' }}>
              {dish.name}
            </h2>
            {dish.restaurant?.name && (
              <p className="text-sm text-gray-900 font-normal">{dish.restaurant.name}</p>
            )}
          </div>

          {dish.description && (
            <p className="text-gray-700 mb-6 leading-relaxed">
              {dish.description}
            </p>
          )}

          {/* Suppléments (réels depuis l'API) */}
          {supplementsLoading && (
            <p className="text-sm text-gray-900 font-normal mb-4">{t('restaurant.loadingSupplements')}</p>
          )}
          {!supplementsLoading && supplements.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3" style={{ color: 'rgb(5, 70, 37)' }}>
                {t('restaurant.supplements')}
              </h3>
              <div className="space-y-3">
                {supplements.map((supplement) => (
                  <label
                    key={supplement.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedSupplements[supplement.id] || false}
                        onChange={() => handleSupplementToggle(supplement.id)}
                        className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="font-medium">{supplement.name}</span>
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'rgb(5, 70, 37)' }}>
                      +{Number(supplement.price).toFixed(2)}€
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Quantité et prix */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <span className="font-medium">{t('restaurant.quantity')}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <Minus size={16} />
                </button>
                <span className="w-8 text-center font-medium">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
            <div className="text-2xl font-bold" style={{ color: 'rgb(5, 70, 37)' }}>
              {calculateTotalPrice().toFixed(2)}€
            </div>
          </div>

          {/* Bouton Ajouter au panier */}
          <button
            onClick={handleAddToCart}
            className="w-full py-4 rounded-xl font-bold text-lg transition-colors flex items-center justify-center gap-2 hover:opacity-90"
            style={{ backgroundColor: 'rgb(5, 70, 37)', color: 'white' }}
          >
            <Plus size={20} />
            {t('home.addToCart')}
          </button>

          {/* Lien vers détails */}
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              navigate(`/product/${dish.id}`);
              onClose();
            }}
            className="w-full py-3 rounded-xl font-medium text-base transition-colors flex items-center justify-center gap-2 mt-3 border-2 hover:bg-green-50"
            style={{ backgroundColor: 'white', color: 'rgb(5, 70, 37)', borderColor: 'rgb(5, 70, 37)' }}
          >
            <MessageCircle size={18} />
            {t('restaurant.viewDetailsAndReview')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DishPopup;
